import React, { useEffect, useMemo, useRef, useState } from 'react';
import marketingDocumentApi from 'api/marketingDocumentApi';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';

const typeOptions = [
  { value: '', label: 'Tất cả loại' },
  { value: 'BANNER', label: 'Tài liệu' },
  { value: 'POLICY', label: 'Chính sách hoa hồng' },
  { value: 'GUIDE', label: 'Hướng dẫn sử dụng' },
];

const statusOptions = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'PUBLISHED', label: 'Đang hiển thị' },
  { value: 'DRAFT', label: 'Nháp' },
];

const emptyForm = {
  title: '',
  description: '',
  type: 'BANNER',
  status: 'PUBLISHED',
  fileUrl: '',
  fileName: '',
  thumbnailUrl: '',
  sortOrder: 0,
};

const getTypeLabel = (type) => {
  const normalized = String(type || '').toUpperCase();
  if (['BANNER', 'IMAGE', 'VIDEO'].includes(normalized)) return 'Tài liệu';
  if (normalized === 'POLICY') return 'Chính sách hoa hồng';
  if (normalized === 'GUIDE') return 'Hướng dẫn sử dụng';
  return type || '-';
};
const getStatusLabel = (status) => (status === 'PUBLISHED' ? 'Đang hiển thị' : 'Nháp');
const isPolicyType = (type) => String(type || '').toUpperCase() === 'POLICY';
const isGuideType = (type) => String(type || '').toUpperCase() === 'GUIDE';
const hasOpenableFile = (fileUrl = '') => {
  const value = String(fileUrl || '').trim();
  return value && !value.startsWith('#');
};

const parseDescription = (description = '') => {
  const text = String(description || '').trim();
  if (!text.startsWith('{') || !text.endsWith('}')) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

const getDocumentDescriptionText = (document = {}) => {
  const parsed = parseDescription(document.description);
  if (parsed) return parsed.plainDescription || parsed.summary || parsed.content || document.fileName || document.fileUrl || '';
  return document.description || document.fileName || document.fileUrl || '';
};

const getFileKind = (fileUrl = '') => {
  const lower = String(fileUrl).toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/.test(lower)) return 'image';
  if (/\.(mp4|mov|avi|mkv|webm)(\?|$)/.test(lower)) return 'video';
  if (/\.pdf(\?|$)/.test(lower)) return 'pdf';
  return 'file';
};

function MarketingDocsScreen() {
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filters, setFilters] = useState({
    q: '',
    type: '',
    status: '',
  });

  const emptyCampaignForm = {
    pinnedToCommerce: false,
    campaignCode: '',
    applyTarget: '',
    statusText: 'Đang diễn ra',
    timeText: '',
    recordFormat: '',
    payoutTime: '',
    plainDescription: '',
    benefit1Value: '',
    benefit1Sub: '',
    benefit1Icon: 'money',
    benefit2Value: '',
    benefit2Sub: '',
    benefit2Icon: 'trending',
    benefit3Value: '',
    benefit3Sub: '',
    benefit3Icon: 'support',
    step1Title: 'Đăng ký tham gia',
    step1Sub: '',
    step1Icon: 'assignment',
    step2Title: 'Giới thiệu khách hàng',
    step2Sub: '',
    step2Icon: 'people',
    step3Title: 'Giao dịch thành công',
    step3Sub: '',
    step3Icon: 'handshake',
    step4Title: 'Nhận hoa hồng',
    step4Sub: '',
    step4Icon: 'gift',
    conditionsText: '',
    mechanismsText: '',
    notesText: '',
    supportHotline: '0886 322 622',
    supportEmail: 'support@evg.vn',
  };

  const [campaignForm, setCampaignForm] = useState(emptyCampaignForm);

  const publishedCount = useMemo(
    () => documents.filter((item) => item.status === 'PUBLISHED').length,
    [documents]
  );

  const loadDocuments = () => {
    setLoading(true);
    marketingDocumentApi.getAdminDocuments({ page: 0, limit: 100, ...filters })
      .then((response) => {
        if (response?.result === 'success') {
          setDocuments(response.documents || []);
          setTotal(response.total || 0);
        }
      })
      .catch((error) => {
        ToastHelper.showError(error?.response?.data?.reason || 'Không tải được kho tài liệu marketing');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const openCreateModal = () => {
    setEditingDocument(null);
    setForm(emptyForm);
    setUploadedFiles([]);
    setCampaignForm(emptyCampaignForm);
    setModalOpen(true);
  };

  const openEditModal = (document) => {
    setEditingDocument(document);
    
    let campaignData = emptyCampaignForm;
    let plainDesc = document.description || '';
    
    try {
      const parsed = parseDescription(document.description);
      if (parsed) {
        if (parsed.isRichCampaign || parsed.isPolicyDocument || parsed.isGuideDocument) {
          const b1 = parsed.benefits?.[0] || {};
          const b2 = parsed.benefits?.[1] || {};
          const b3 = parsed.benefits?.[2] || {};
          const s1 = parsed.steps?.[0] || {};
          const s2 = parsed.steps?.[1] || {};
          const s3 = parsed.steps?.[2] || {};
          const s4 = parsed.steps?.[3] || {};
          
          plainDesc = parsed.plainDescription || parsed.summary || parsed.content || '';
          
          campaignData = {
            pinnedToCommerce: Boolean(parsed.pinnedToCommerce),
            campaignCode: parsed.campaignCode || '',
            applyTarget: parsed.applyTarget || '',
            statusText: parsed.statusText || 'Đang diễn ra',
            timeText: parsed.timeText || '',
            recordFormat: parsed.recordFormat || '',
            payoutTime: parsed.payoutTime || '',
            plainDescription: plainDesc,
            benefit1Value: b1.value || '',
            benefit1Sub: b1.subtext || '',
            benefit1Icon: b1.icon || 'money',
            benefit2Value: b2.value || '',
            benefit2Sub: b2.subtext || '',
            benefit2Icon: b2.icon || 'trending',
            benefit3Value: b3.value || '',
            benefit3Sub: b3.subtext || '',
            benefit3Icon: b3.icon || 'support',
            step1Title: s1.title || 'Đăng ký tham gia',
            step1Sub: s1.subtext || '',
            step1Icon: s1.icon || 'assignment',
            step2Title: s2.title || 'Giới thiệu khách hàng',
            step2Sub: s2.subtext || '',
            step2Icon: s2.icon || 'people',
            step3Title: s3.title || 'Giao dịch thành công',
            step3Sub: s3.subtext || '',
            step3Icon: s3.icon || 'handshake',
            step4Title: s4.title || 'Nhận hoa hồng',
            step4Sub: s4.subtext || '',
            step4Icon: s4.icon || 'gift',
            conditionsText: Array.isArray(parsed.conditions) ? parsed.conditions.join('\n') : '',
            mechanismsText: Array.isArray(parsed.mechanisms) ? parsed.mechanisms.join('\n') : '',
            notesText: Array.isArray(parsed.notes) ? parsed.notes.join('\n') : '',
            supportHotline: parsed.supportHotline || '0886 322 622',
            supportEmail: parsed.supportEmail || 'support@evg.vn',
          };
        } else {
          plainDesc = parsed.plainDescription || '';
          campaignData = {
            ...emptyCampaignForm,
            pinnedToCommerce: Boolean(parsed.pinnedToCommerce),
          };
        }
      }
    } catch (e) {
      console.error('Lỗi phân tích JSON mô tả chiến dịch:', e);
    }

    setForm({
      title: document.title || '',
      description: plainDesc,
      type: document.type || 'BANNER',
      status: document.status || 'PUBLISHED',
      fileUrl: document.fileUrl || '',
      fileName: document.fileName || '',
      thumbnailUrl: document.thumbnailUrl || '',
      sortOrder: document.sortOrder || 0,
    });
    
    setCampaignForm(campaignData);
    setUploadedFiles([]);
    setModalOpen(true);
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length > 0) {
        const lastFile = updated[updated.length - 1];
        setForm((f) => ({
          ...f,
          fileUrl: lastFile.url,
          fileName: lastFile.name,
          thumbnailUrl: lastFile.thumbnailUrl,
        }));
      } else {
        setForm((f) => ({
          ...f,
          fileUrl: '',
          fileName: '',
          thumbnailUrl: '',
        }));
      }
      return updated;
    });
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      if (editingDocument) {
        // Edit Mode: only support 1 file
        const file = files[0];
        const media = await miscHelper.callApiUploadFile(file);
        const firstMedia = Array.isArray(media) ? media[0] : null;
        if (!firstMedia) {
          ToastHelper.showError('Không upload được file');
          return;
        }
        const fileUrl = Utils.getFullUrl(firstMedia.relativePath || firstMedia.filePath || '');
        const fileName = file.name;
        const thumbnailUrl = getFileKind(file.name) === 'image' ? fileUrl : '';
        setForm((prev) => ({
          ...prev,
          fileUrl,
          fileName,
          thumbnailUrl,
        }));
        ToastHelper.showSuccess('Đã upload file');
      } else {
        // Create Mode: support multiple files
        const newUploadedFiles = [...uploadedFiles];
        let successCount = 0;
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            const media = await miscHelper.callApiUploadFile(file);
            const firstMedia = Array.isArray(media) ? media[0] : null;
            if (firstMedia) {
              const fileUrl = Utils.getFullUrl(firstMedia.relativePath || firstMedia.filePath || '');
              const fileName = file.name;
              const thumbnailUrl = getFileKind(file.name) === 'image' ? fileUrl : '';
              newUploadedFiles.push({
                name: fileName,
                url: fileUrl,
                thumbnailUrl: thumbnailUrl,
              });
              successCount++;
            }
          } catch (err) {
            console.error(`Lỗi upload file ${file.name}:`, err);
          }
        }
        
        setUploadedFiles(newUploadedFiles);
        // If it's a single upload, also update form for backward compatibility
        if (newUploadedFiles.length > 0) {
          const lastFile = newUploadedFiles[newUploadedFiles.length - 1];
          setForm((prev) => ({
            ...prev,
            fileUrl: lastFile.url,
            fileName: lastFile.name,
            thumbnailUrl: lastFile.thumbnailUrl,
          }));
        }
        
        if (successCount > 0) {
          ToastHelper.showSuccess(`Đã upload thành công ${successCount}/${files.length} file`);
        } else {
          ToastHelper.showError('Upload tất cả file thất bại');
        }
      }
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason || 'Upload file thất bại');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const saveDocument = async () => {
    const isPolicy = isPolicyType(form.type);
    const isGuide = isGuideType(form.type);
    const isStructuredContent = isPolicy || isGuide;
    // Basic validations
    if (isStructuredContent) {
      if (!form.title.trim()) {
        ToastHelper.showError(isPolicy ? 'Vui lòng nhập tiêu đề chính sách' : 'Vui lòng nhập tiêu đề hướng dẫn');
        return;
      }
      if (!form.description.trim() && !campaignForm.conditionsText.trim() && !campaignForm.mechanismsText.trim()) {
        ToastHelper.showError(isPolicy ? 'Vui lòng nhập nội dung hoặc điều kiện chính sách' : 'Vui lòng nhập tóm tắt hoặc các bước hướng dẫn');
        return;
      }
    } else if (editingDocument) {
      if (!form.title.trim()) {
        ToastHelper.showError('Vui lòng nhập tiêu đề tài liệu');
        return;
      }
      if (!form.fileUrl.trim()) {
        ToastHelper.showError('Vui lòng tải file tài liệu');
        return;
      }
    } else if (uploadedFiles.length === 0 && !form.fileUrl.trim()) {
      ToastHelper.showError('Vui lòng tải file tài liệu');
      return;
    }

    setSaving(true);
    try {
      let finalDescription = form.description;
      if (isStructuredContent) {
        const conditions = campaignForm.conditionsText.split('\n').map(line => line.trim()).filter(Boolean);
        const mechanisms = campaignForm.mechanismsText.split('\n').map(line => line.trim()).filter(Boolean);
        const notes = campaignForm.notesText.split('\n').map(line => line.trim()).filter(Boolean);
        
        finalDescription = JSON.stringify({
          isPolicyDocument: isPolicy,
          isGuideDocument: isGuide,
          pinnedToCommerce: false,
          plainDescription: form.description,
          campaignCode: campaignForm.campaignCode,
          applyTarget: campaignForm.applyTarget,
          statusText: campaignForm.statusText,
          timeText: campaignForm.timeText,
          recordFormat: campaignForm.recordFormat,
          payoutTime: campaignForm.payoutTime,
          conditions,
          mechanisms,
          notes,
          supportHotline: campaignForm.supportHotline,
          supportEmail: campaignForm.supportEmail,
        });
      } else if (campaignForm.pinnedToCommerce) {
        finalDescription = JSON.stringify({
          pinnedToCommerce: true,
          plainDescription: form.description,
        });
      }

      const basePayload = {
        ...form,
        description: finalDescription,
        fileUrl: isStructuredContent ? (form.fileUrl || (isPolicy ? '#policy-content' : '#guide-content')) : form.fileUrl,
        fileName: isStructuredContent ? (form.fileName || (isPolicy ? 'Chính sách nhập trực tiếp' : 'Hướng dẫn nhập trực tiếp')) : form.fileName,
      };

      if (editingDocument) {
        await marketingDocumentApi.updateDocument(editingDocument.marketingDocumentId, {
          ...basePayload,
        });
        ToastHelper.showSuccess(isPolicy ? 'Đã cập nhật chính sách' : isGuide ? 'Đã cập nhật hướng dẫn' : 'Đã cập nhật tài liệu');
      } else {
        // Create Mode
        if (isStructuredContent) {
          await marketingDocumentApi.createDocument({
            ...basePayload,
            title: form.title.trim(),
          });
          ToastHelper.showSuccess(isPolicy ? 'Đã thêm chính sách' : 'Đã thêm hướng dẫn');
        } else if (uploadedFiles.length > 0) {
          // Loop and create documents for each uploaded file
          for (const fileItem of uploadedFiles) {
            const documentTitle = form.title.trim() 
              ? form.title.trim() 
              : fileItem.name.replace(/\.[^/.]+$/, ""); // strip file extension
            
            await marketingDocumentApi.createDocument({
              ...basePayload,
              description: finalDescription,
              title: documentTitle,
              fileUrl: fileItem.url,
              fileName: fileItem.name,
              thumbnailUrl: fileItem.thumbnailUrl || fileItem.url,
            });
          }
          ToastHelper.showSuccess(`Đã lưu ${uploadedFiles.length} tài liệu`);
        } else {
          // Fallback legacy behavior
          const documentTitle = form.title.trim() 
            ? form.title.trim() 
            : form.fileName.replace(/\.[^/.]+$/, "") || 'Tài liệu không tên';
          
          await marketingDocumentApi.createDocument({
            ...form,
            description: finalDescription,
            title: documentTitle,
          });
          ToastHelper.showSuccess('Đã thêm tài liệu');
        }
      }
      setModalOpen(false);
      loadDocuments();
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason || 'Không lưu được tài liệu');
    } finally {
      setSaving(false);
    }
  };

  const deleteDocument = async (document) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Xóa tài liệu "${document.title}"?`)) return;

    try {
      await marketingDocumentApi.deleteDocument(document.marketingDocumentId);
      ToastHelper.showSuccess('Đã xóa tài liệu');
      loadDocuments();
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason || 'Không xóa được tài liệu');
    }
  };

  const submitFilter = (event) => {
    event.preventDefault();
    loadDocuments();
  };

  const renderPreview = (document) => {
    const kind = getFileKind(document.fileUrl);
    if (kind === 'image') {
      return <img src={document.thumbnailUrl || document.fileUrl} alt={document.title} style={styles.thumbImage} />;
    }
    if (kind === 'video') {
      return <i className="fa-solid fa-circle-play" style={styles.thumbIcon} />;
    }
    if (kind === 'pdf') {
      return <i className="fa-solid fa-file-pdf" style={{ ...styles.thumbIcon, color: '#ef4444' }} />;
    }
    return <i className="fa-solid fa-file-lines" style={styles.thumbIcon} />;
  };

  const isPolicyForm = isPolicyType(form.type);
  const isGuideForm = isGuideType(form.type);
  const isStructuredForm = isPolicyForm || isGuideForm;

  return (
    <KT01BaseLayout isFullScreen={true}>
      <div className="container-fluid px-4 pb-4 mt-4" style={styles.page}>
      <style>
        {`
          .marketing-docs-table th {
            background: #f8fafc;
            color: #4b5268;
            font-weight: 800;
            padding: 14px 16px;
            border-bottom: 1px solid #e6e9f3;
            white-space: nowrap;
          }
          .marketing-docs-table td {
            padding: 14px 16px;
            border-bottom: 1px solid #edf0f6;
            color: #4b5268;
            vertical-align: middle;
          }
          .marketing-docs-table tr:hover td {
            background: #fbfcff;
          }
          .marketing-docs-form input,
          .marketing-docs-form select,
          .marketing-docs-form textarea,
          .marketing-docs-toolbar select {
            min-height: 44px;
            border: 1px solid #dbe1ee;
            border-radius: 8px;
            padding: 0 14px;
            color: #151a30;
            outline: none;
            background: #fff;
            width: 100%;
          }
          .marketing-docs-form textarea {
            min-height: 104px;
            padding-top: 12px;
            resize: vertical;
          }
          .marketing-docs-form input:focus,
          .marketing-docs-form select:focus,
          .marketing-docs-form textarea:focus,
          .marketing-docs-toolbar select:focus,
          .marketing-docs-search:focus-within {
            border-color: #31c878;
            box-shadow: 0 0 0 3px rgba(49, 200, 120, 0.12);
          }
          .marketing-docs-search input {
            border: 0;
            outline: none;
            min-height: 42px;
            flex: 1;
            color: #151a30;
          }
          @media (max-width: 1200px) {
            .marketing-docs-toolbar {
              grid-template-columns: 1fr 1fr;
            }
          }
          @media (max-width: 768px) {
            .marketing-docs-toolbar,
            .marketing-docs-form-grid,
            .marketing-docs-stats {
              grid-template-columns: 1fr !important;
            }
            .marketing-docs-header,
            .marketing-docs-upload {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
      <div className="marketing-docs-header" style={styles.headerCard}>
        <div>
          <div style={styles.eyebrow}>Affiliate Marketing</div>
          <h2 style={styles.title}>Kho Tài liệu Marketing</h2>
          <p style={styles.subtitle}>
            Upload PDF chính sách, banner quảng cáo, ảnh sản phẩm và video hướng dẫn để đồng bộ sang màn Công cụ Affiliate.
          </p>
        </div>
        <button type="button" style={styles.primaryButton} onClick={openCreateModal}>
          <i className="fa-solid fa-plus" />
          Thêm tài liệu
        </button>
      </div>

      <div className="marketing-docs-stats" style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span>Tổng tài liệu</span>
          <strong>{total}</strong>
        </div>
        <div style={styles.statCard}>
          <span>Đang hiển thị</span>
          <strong>{publishedCount}</strong>
        </div>
        <div style={styles.statCard}>
          <span>Loại tài liệu</span>
          <strong>{typeOptions.length - 1}</strong>
        </div>
      </div>

      <div style={styles.panel}>
        <form className="marketing-docs-toolbar" style={styles.toolbar} onSubmit={submitFilter}>
          <div className="marketing-docs-search" style={styles.searchBox}>
            <i className="fa-solid fa-search" />
            <input
              value={filters.q}
              onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
              placeholder="Tìm theo tiêu đề, mô tả"
            />
          </div>
          <select value={filters.type} onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}>
            {typeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}>
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <button type="submit" style={styles.searchButton}>Tìm kiếm</button>
        </form>

        <div style={styles.tableWrap}>
          <table className="marketing-docs-table" style={styles.table}>
            <thead>
              <tr>
                <th>File</th>
                <th>Tiêu đề</th>
                <th>Loại</th>
                <th>Trạng thái</th>
                <th>Thứ tự</th>
                <th>Người tạo</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={styles.emptyCell}>Đang tải tài liệu...</td></tr>
              ) : documents.length ? documents.map((document) => (
                <tr key={document.marketingDocumentId}>
                  <td>
                    <div style={styles.thumbBox}>{renderPreview(document)}</div>
                  </td>
                  <td>
                    <div style={styles.docTitle}>{document.title}</div>
                    <div style={styles.docDesc}>{getDocumentDescriptionText(document)}</div>
                  </td>
                  <td>{getTypeLabel(document.type)}</td>
                  <td>
                    <span style={document.status === 'PUBLISHED' ? styles.statusPublished : styles.statusDraft}>
                      {getStatusLabel(document.status)}
                    </span>
                  </td>
                  <td>{document.sortOrder || 0}</td>
                  <td>{document.creator?.fullname || document.creator?.email || '-'}</td>
                  <td>
                    <div style={styles.actions}>
                      <button
                        type="button"
                        title={hasOpenableFile(document.fileUrl) ? 'Mở file' : 'Không có file đính kèm'}
                        onClick={() => hasOpenableFile(document.fileUrl) && window.open(document.fileUrl, '_blank')}
                        style={{ ...styles.iconButton, opacity: hasOpenableFile(document.fileUrl) ? 1 : 0.45 }}
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square" />
                      </button>
                      <button type="button" title="Sửa" onClick={() => openEditModal(document)} style={styles.iconButton}>
                        <i className="fa-regular fa-pen-to-square" />
                      </button>
                      <button type="button" title="Xóa" onClick={() => deleteDocument(document)} style={styles.iconButtonDanger}>
                        <i className="fa-regular fa-trash-can" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} style={styles.emptyCell}>Chưa có tài liệu marketing nào.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div style={styles.modalBackdrop}>
          <div className="marketing-docs-form" style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editingDocument ? 'Cập nhật tài liệu' : 'Thêm tài liệu Marketing'}</h3>
              <button type="button" style={styles.closeButton} onClick={() => setModalOpen(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div className="marketing-docs-form-grid" style={styles.formGrid}>
                <label style={styles.field}>
                  <span>Loại nội dung</span>
                  <select value={form.type} onChange={(event) => updateForm('type', event.target.value)}>
                    {typeOptions.filter((option) => option.value).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label style={styles.field}>
                  <span>Trạng thái</span>
                  <select value={form.status} onChange={(event) => updateForm('status', event.target.value)}>
                    {statusOptions.filter((option) => option.value).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="marketing-docs-upload" style={styles.uploadArea}>
                <div style={styles.uploadPreview}>
                  {hasOpenableFile(form.fileUrl) ? (
                    getFileKind(form.fileUrl) === 'image' ? (
                      <img src={form.thumbnailUrl || form.fileUrl} alt={form.title || 'document'} style={styles.uploadImage} />
                    ) : (
                      <i className={getFileKind(form.fileUrl) === 'video' ? 'fa-solid fa-circle-play' : 'fa-solid fa-file-lines'} style={styles.uploadIcon} />
                    )
                  ) : isPolicyForm ? (
                    <i className="fa-solid fa-shield-halved" style={styles.uploadIcon} />
                  ) : isGuideForm ? (
                    <i className="fa-solid fa-book-open" style={styles.uploadIcon} />
                  ) : (
                    <i className="fa-solid fa-cloud-arrow-up" style={styles.uploadIcon} />
                  )}
                </div>
                <div>
                  <strong>{isPolicyForm ? 'File đính kèm chính sách' : isGuideForm ? 'File đính kèm hướng dẫn' : 'File tài liệu *'}</strong>
                  <p>{isStructuredForm ? 'Có thể bỏ trống nếu nội dung được nhập trực tiếp bên dưới.' : 'Hỗ trợ PDF, ảnh, video hướng dẫn hoặc tài liệu bán hàng.'}</p>
                  {form.fileName || hasOpenableFile(form.fileUrl) ? <span style={styles.fileName}>{form.fileName || form.fileUrl}</span> : null}
                  <button type="button" style={styles.uploadButton} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? 'Đang upload...' : 'Chọn file'}
                  </button>
                  <input 
                    ref={fileInputRef} 
                    type="file" 
                    multiple={!editingDocument && !isStructuredForm}
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.avi,.mkv" 
                    onChange={handleUpload} 
                    style={{ display: 'none' }} 
                  />
                </div>
              </div>

              {!editingDocument && !isStructuredForm && uploadedFiles.length > 0 && (
                <div style={styles.uploadedFilesContainer}>
                  <strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#4b5268' }}>
                    Danh sách file đã chọn ({uploadedFiles.length}):
                  </strong>
                  <div style={styles.uploadedFilesGrid}>
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} style={styles.fileCard}>
                        <div style={styles.fileCardPreview}>
                          {getFileKind(file.url) === 'image' ? (
                            <img src={file.thumbnailUrl || file.url} alt={file.name} style={styles.fileCardImage} />
                          ) : (
                            <i className={getFileKind(file.url) === 'video' ? 'fa-solid fa-circle-play' : 'fa-solid fa-file-lines'} style={{ fontSize: 24, color: '#31c878' }} />
                          )}
                          <button 
                            type="button" 
                            style={styles.fileCardDeleteBtn} 
                            onClick={() => removeUploadedFile(idx)}
                            title="Xóa file này"
                          >
                            <i className="fa-solid fa-xmark" style={{ fontSize: 10 }} />
                          </button>
                        </div>
                        <span style={styles.fileCardName} title={file.name}>
                          {file.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <label style={styles.field}>
                <span>{isPolicyForm ? 'Tiêu đề chính sách *' : isGuideForm ? 'Tiêu đề hướng dẫn *' : `Tiêu đề ${editingDocument ? '*' : '(Để trống sẽ tự động lấy tên file)'}`}</span>
                <input value={form.title} onChange={(event) => updateForm('title', event.target.value)} placeholder={isPolicyForm ? 'VD: Chính sách hoa hồng trụ sạc 2026' : isGuideForm ? 'VD: Hướng dẫn sử dụng liên kết giới thiệu' : 'VD: Bộ tài liệu giới thiệu sản phẩm'} />
              </label>

              <div className="marketing-docs-form-grid" style={styles.formGrid}>
                <label style={styles.field}>
                  <span>Thứ tự hiển thị</span>
                  <input type="number" value={form.sortOrder} onChange={(event) => updateForm('sortOrder', event.target.value)} />
                </label>
                <label style={styles.field}>
                  <span>{isStructuredForm ? 'Ảnh minh họa (nếu có)' : 'Ảnh thumbnail (nếu có)'}</span>
                  <input value={form.thumbnailUrl} onChange={(event) => updateForm('thumbnailUrl', event.target.value)} placeholder="URL thumbnail cho video/PDF" />
                </label>
              </div>

              {!isStructuredForm ? (
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={Boolean(campaignForm.pinnedToCommerce)}
                    onChange={(event) => setCampaignForm(prev => ({ ...prev, pinnedToCommerce: event.target.checked }))}
                  />
                  <span>Ghim tài liệu này lên banner sản phẩm ở màn Đơn hàng - Sản phẩm</span>
                </label>
              ) : null}

              {isPolicyForm ? (
                <div style={styles.richForm}>
                  <h4 style={styles.sectionHeader}>Thông tin chính sách hoa hồng</h4>
                  <div className="marketing-docs-form-grid" style={styles.formGrid}>
                    <label style={styles.field}>
                      <span>Mã chính sách</span>
                      <input 
                        value={campaignForm.campaignCode} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, campaignCode: e.target.value }))} 
                        placeholder="VD: EVG-CS-0726"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Đối tượng áp dụng</span>
                      <input 
                        value={campaignForm.applyTarget} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, applyTarget: e.target.value }))} 
                        placeholder="VD: CTV / Đối tác EVG / Chủ mặt bằng"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Tình trạng chính sách</span>
                      <select 
                        value={campaignForm.statusText} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, statusText: e.target.value }))}
                      >
                        <option value="Đang diễn ra">Đang diễn ra</option>
                        <option value="Sắp diễn ra">Sắp diễn ra</option>
                        <option value="Đã kết thúc">Đã kết thúc</option>
                      </select>
                    </label>
                    <label style={styles.field}>
                      <span>Thời gian áp dụng</span>
                      <input 
                        value={campaignForm.timeText} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, timeText: e.target.value }))} 
                        placeholder="VD: 01/07/2026 - 31/07/2026"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Hình thức ghi nhận</span>
                      <input 
                        value={campaignForm.recordFormat} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, recordFormat: e.target.value }))} 
                        placeholder="VD: Theo hợp đồng trạm sạc thành công" 
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Thời gian chi trả</span>
                      <input 
                        value={campaignForm.payoutTime} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, payoutTime: e.target.value }))} 
                        placeholder="VD: Trong vòng 7 - 15 ngày làm việc"
                      />
                    </label>
                  </div>

                  <label style={{ ...styles.field, marginTop: 12 }}>
                    <span>Tóm tắt chính sách *</span>
                    <textarea 
                      value={form.description}
                      onChange={(event) => updateForm('description', event.target.value)}
                      placeholder="Tóm tắt ngắn nội dung chính sách để đối tác đọc nhanh..."
                    />
                  </label>

                  <div className="marketing-docs-form-grid" style={{ ...styles.formGrid, marginTop: 12 }}>
                    <label style={styles.field}>
                      <span>Điều kiện áp dụng (Mỗi dòng là 1 điều kiện)</span>
                      <textarea 
                        value={campaignForm.conditionsText} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, conditionsText: e.target.value }))} 
                        placeholder="Mỗi dòng là 1 gạch đầu dòng điều kiện..." 
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Cơ chế ghi nhận & chi trả (Mỗi dòng là 1 cơ chế)</span>
                      <textarea 
                        value={campaignForm.mechanismsText} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, mechanismsText: e.target.value }))} 
                        placeholder="Mỗi dòng là 1 gạch đầu dòng cơ chế..." 
                      />
                    </label>
                  </div>

                  <div className="marketing-docs-form-grid" style={{ ...styles.formGrid, marginTop: 12 }}>
                    <label style={styles.field}>
                      <span>Lưu ý & điều khoản (Mỗi dòng là 1 lưu ý)</span>
                      <textarea 
                        value={campaignForm.notesText} 
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, notesText: e.target.value }))} 
                        placeholder="Mỗi dòng là 1 gạch đầu dòng lưu ý..." 
                      />
                    </label>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <label style={styles.field}>
                        <span>Hotline hỗ trợ</span>
                        <input value={campaignForm.supportHotline} onChange={(e) => setCampaignForm(prev => ({ ...prev, supportHotline: e.target.value }))} />
                      </label>
                      <label style={styles.field}>
                        <span>Email hỗ trợ</span>
                        <input value={campaignForm.supportEmail} onChange={(e) => setCampaignForm(prev => ({ ...prev, supportEmail: e.target.value }))} />
                      </label>
                    </div>
                  </div>
                </div>
              ) : isGuideForm ? (
                <div style={styles.richForm}>
                  <h4 style={styles.sectionHeader}>Thông tin hướng dẫn sử dụng</h4>
                  <div className="marketing-docs-form-grid" style={styles.formGrid}>
                    <label style={styles.field}>
                      <span>Mã hướng dẫn</span>
                      <input
                        value={campaignForm.campaignCode}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, campaignCode: e.target.value }))}
                        placeholder="VD: EVG-HD-REF"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Đối tượng áp dụng</span>
                      <input
                        value={campaignForm.applyTarget}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, applyTarget: e.target.value }))}
                        placeholder="VD: Đối tác mới / Chủ mặt bằng / CTV"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Trạng thái hướng dẫn</span>
                      <select
                        value={campaignForm.statusText}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, statusText: e.target.value }))}
                      >
                        <option value="Đang áp dụng">Đang áp dụng</option>
                        <option value="Cần cập nhật">Cần cập nhật</option>
                        <option value="Ngừng hiển thị">Ngừng hiển thị</option>
                      </select>
                    </label>
                    <label style={styles.field}>
                      <span>Thời lượng đọc/xem</span>
                      <input
                        value={campaignForm.timeText}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, timeText: e.target.value }))}
                        placeholder="VD: 3 phút đọc / 5 phút xem"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Mục tiêu hướng dẫn</span>
                      <input
                        value={campaignForm.recordFormat}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, recordFormat: e.target.value }))}
                        placeholder="VD: Biết cách sao chép và chia sẻ link giới thiệu"
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Khi nào nên dùng</span>
                      <input
                        value={campaignForm.payoutTime}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, payoutTime: e.target.value }))}
                        placeholder="VD: Khi gửi tài liệu cho khách lần đầu"
                      />
                    </label>
                  </div>

                  <label style={{ ...styles.field, marginTop: 12 }}>
                    <span>Tóm tắt hướng dẫn *</span>
                    <textarea
                      value={form.description}
                      onChange={(event) => updateForm('description', event.target.value)}
                      placeholder="Tóm tắt ngắn nội dung hướng dẫn để đối tác đọc nhanh..."
                    />
                  </label>

                  <div className="marketing-docs-form-grid" style={{ ...styles.formGrid, marginTop: 12 }}>
                    <label style={styles.field}>
                      <span>Các bước thực hiện (Mỗi dòng là 1 bước)</span>
                      <textarea
                        value={campaignForm.conditionsText}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, conditionsText: e.target.value }))}
                        placeholder="Bước 1: ...&#10;Bước 2: ..."
                      />
                    </label>
                    <label style={styles.field}>
                      <span>Mẹo sử dụng / gợi ý nội dung (Mỗi dòng là 1 mẹo)</span>
                      <textarea
                        value={campaignForm.mechanismsText}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, mechanismsText: e.target.value }))}
                        placeholder="Mẹo 1: ...&#10;Mẹo 2: ..."
                      />
                    </label>
                  </div>

                  <div className="marketing-docs-form-grid" style={{ ...styles.formGrid, marginTop: 12 }}>
                    <label style={styles.field}>
                      <span>Lưu ý thêm (Mỗi dòng là 1 lưu ý)</span>
                      <textarea
                        value={campaignForm.notesText}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, notesText: e.target.value }))}
                        placeholder="Lưu ý khi gửi khách / quy chuẩn thương hiệu..."
                      />
                    </label>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <label style={styles.field}>
                        <span>Hotline hỗ trợ</span>
                        <input value={campaignForm.supportHotline} onChange={(e) => setCampaignForm(prev => ({ ...prev, supportHotline: e.target.value }))} />
                      </label>
                      <label style={styles.field}>
                        <span>Email hỗ trợ</span>
                        <input value={campaignForm.supportEmail} onChange={(e) => setCampaignForm(prev => ({ ...prev, supportEmail: e.target.value }))} />
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <label style={styles.field}>
                  <span>Mô tả / nội dung gợi ý</span>
                  <textarea value={form.description} onChange={(event) => updateForm('description', event.target.value)} placeholder="Mô tả ngắn để CTV biết dùng tài liệu này khi nào" />
                </label>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button type="button" style={styles.cancelButton} onClick={() => setModalOpen(false)}>Hủy bỏ</button>
              <button type="button" style={styles.primaryButton} onClick={saveDocument} disabled={saving || uploading}>
                {saving ? 'Đang lưu...' : 'Lưu tài liệu'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </KT01BaseLayout>
  );
}

const styles = {
  page: {
    color: '#151a30',
  },
  headerCard: {
    background: '#fff',
    borderRadius: 8,
    padding: '18px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    boxShadow: '0 4px 14px rgba(18, 38, 63, 0.06)',
    border: '1px solid #e6e9f3',
  },
  eyebrow: {
    color: '#25c76f',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 3,
    fontSize: 11,
    marginBottom: 8,
  },
  title: {
    fontSize: 22,
    lineHeight: 1.25,
    fontWeight: 700,
    margin: 0,
    color: '#3f4254',
  },
  subtitle: {
    margin: '8px 0 0',
    color: '#7b8197',
    fontSize: 14,
    lineHeight: 1.45,
    maxWidth: 760,
  },
  primaryButton: {
    border: 0,
    borderRadius: 8,
    background: '#31c878',
    color: '#fff',
    fontWeight: 800,
    padding: '12px 18px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    minHeight: 44,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 16,
    marginTop: 18,
  },
  statCard: {
    background: '#fff',
    border: '1px solid #e6e9f3',
    borderRadius: 8,
    padding: '14px 16px',
    boxShadow: '0 4px 14px rgba(18, 38, 63, 0.04)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#3f4254',
    fontSize: 14,
  },
  panel: {
    background: '#fff',
    border: '1px solid #e6e9f3',
    borderRadius: 8,
    padding: 16,
    marginTop: 18,
    boxShadow: '0 4px 14px rgba(18, 38, 63, 0.04)',
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 1fr) 220px 220px 140px',
    gap: 12,
    marginBottom: 16,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    border: '1px solid #dbe1ee',
    borderRadius: 8,
    padding: '0 14px',
    minHeight: 44,
    color: '#9aa2b8',
  },
  searchButton: {
    border: 0,
    borderRadius: 8,
    background: '#3699ff',
    color: '#fff',
    fontWeight: 800,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 1040,
  },
  thumbBox: {
    width: 62,
    height: 62,
    borderRadius: 8,
    background: '#ecfff5',
    border: '1px solid #dce9f4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  thumbImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbIcon: {
    fontSize: 26,
    color: '#31c878',
  },
  docTitle: {
    fontWeight: 800,
    color: '#151a30',
  },
  docDesc: {
    color: '#8b91a7',
    marginTop: 6,
    maxWidth: 420,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusPublished: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#16a34a',
    fontWeight: 800,
  },
  statusDraft: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#fff7ed',
    color: '#ea580c',
    fontWeight: 800,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  iconButton: {
    width: 34,
    height: 34,
    border: '1px solid #dbe1ee',
    borderRadius: 8,
    background: '#fff',
    color: '#737b92',
  },
  iconButtonDanger: {
    width: 34,
    height: 34,
    border: '1px solid #fee2e2',
    borderRadius: 8,
    background: '#fff',
    color: '#ef4444',
  },
  emptyCell: {
    textAlign: 'center',
    color: '#8b91a7',
    padding: 44,
    fontWeight: 700,
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modal: {
    width: 'min(920px, 100%)',
    maxHeight: '92vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
  },
  modalHeader: {
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #edf0f6',
  },
  closeButton: {
    width: 40,
    height: 40,
    border: 0,
    borderRadius: 8,
    background: '#f2f5fb',
    color: '#737b92',
  },
  modalBody: {
    padding: 22,
    display: 'grid',
    gap: 16,
  },
  uploadArea: {
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    padding: 16,
    display: 'grid',
    gridTemplateColumns: '150px 1fr',
    gap: 18,
    alignItems: 'center',
    background: '#f8fafc',
  },
  uploadPreview: {
    width: 150,
    height: 120,
    borderRadius: 8,
    background: '#fff',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  uploadImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  uploadIcon: {
    fontSize: 42,
    color: '#31c878',
  },
  fileName: {
    display: 'block',
    color: '#64748b',
    marginBottom: 10,
    wordBreak: 'break-all',
  },
  uploadButton: {
    border: 0,
    borderRadius: 8,
    background: '#e9fff3',
    color: '#19a65b',
    fontWeight: 800,
    padding: '10px 16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
  },
  field: {
    display: 'grid',
    gap: 8,
    color: '#4b5268',
    fontWeight: 800,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    border: '1px solid #d8f5e4',
    borderRadius: 10,
    background: '#f0fdf4',
    color: '#166534',
    fontWeight: 800,
  },
  modalFooter: {
    padding: 22,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    borderTop: '1px solid #edf0f6',
  },
  cancelButton: {
    border: 0,
    borderRadius: 8,
    background: '#eef2f8',
    color: '#4b5268',
    fontWeight: 800,
    padding: '12px 18px',
  },
  uploadedFilesContainer: {
    border: '1px solid #edf0f6',
    borderRadius: 8,
    padding: '12px 14px',
    background: '#fff',
    marginTop: 4,
  },
  uploadedFilesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  fileCard: {
    width: 90,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  fileCardPreview: {
    position: 'relative',
    width: 80,
    height: 70,
    borderRadius: 6,
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  fileCardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  fileCardDeleteBtn: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: 'rgba(239, 68, 68, 0.85)',
    color: '#fff',
    border: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  fileCardName: {
    width: '100%',
    fontSize: 11,
    color: '#64748b',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  richForm: {
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    background: '#f8fafc',
    marginTop: '12px',
  },
  sectionHeader: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#0f172a',
    borderBottom: '2px solid #31c878',
    paddingBottom: '6px',
    marginBottom: '14px',
  },
  subHeader: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#334155',
    marginBottom: '8px',
  },
  boxBorder: {
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '12px',
    background: '#fff',
  },
};

export default MarketingDocsScreen;
